import { PersonCard } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageSidebar/PersonsList/PersonCard.tsx';
import { Badge, Button, Text } from '@mantine/core';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StorageKeys, StorageUtilities } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { GetPersonForCapRes } from '@dealstack/shared/lib/generated';
import { useCalculateElementMaxHeight } from '@dealstack/frontend/shared/other/react/hooks/useCalculateElementMaxHeight.ts';
import {
	StyledLoadMoreContainer,
	StyledVirtualizedListContainer,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageSidebar/PersonsList/components.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';

const PAGE_BOTTOM_PADDING = 32;
const ESTIMATED_PERSON_CARD_MAX_HEIGHT = 100;

interface Props {
	dealId: string;
	persons?: GetPersonForCapRes[];
	selectedPersonId: string;
	setSelectedPersonId: (id: string) => void;
	selectedPersons: Set<string>;
	setSelectedPersons: (ids: Set<string>) => void;
	isManageMode: boolean;
	isCreateMode?: boolean;
	onAddPersonClick: () => void;
}

const PersonsList = ({
	dealId,
	persons,
	selectedPersonId,
	setSelectedPersonId,
	selectedPersons,
	setSelectedPersons,
	isManageMode,
	isCreateMode,
	onAddPersonClick,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const { ref, maxHeight } = useCalculateElementMaxHeight(PAGE_BOTTOM_PADDING);

	const [pinnedPersons, setPinnedPersons] = useState<Set<string>>(new Set());

	useEffect(() => {
		const pins =
			StorageUtilities.getItem<string[]>(StorageKeys.ENTITY_PIN_LOCALSTORAGE_KEY, dealId) ??
			[];

		setPinnedPersons(new Set(pins));
	}, [dealId]);

	const sortedPersons = useMemo(() => {
		if (!persons) {
			return [];
		}

		const pinned: GetPersonForCapRes[] = [];
		const remaining: GetPersonForCapRes[] = [];

		for (const person of persons) {
			if (pinnedPersons.has(person.id)) {
				pinned.push(person);
			} else {
				remaining.push(person);
			}
		}

		return pinned.concat(remaining);
	}, [persons, pinnedPersons]);

	const onPersonCheckboxChange = useCallback(
		(personId: string, isChecked: boolean) => {
			const newSelectedPersons = new Set(selectedPersons);

			if (isChecked) {
				newSelectedPersons.add(personId);
			} else {
				newSelectedPersons.delete(personId);
			}

			setSelectedPersons(newSelectedPersons);
		},
		[selectedPersons, setSelectedPersons]
	);

	const onPinValueChange = useCallback(
		(personId: string, isPinned: boolean) => {
			const newPinnedPersons = new Set(pinnedPersons);

			if (isPinned) {
				newPinnedPersons.add(personId);
			} else {
				newPinnedPersons.delete(personId);
			}

			setPinnedPersons(newPinnedPersons);

			StorageUtilities.setItem(
				StorageKeys.ENTITY_PIN_LOCALSTORAGE_KEY,
				Array.from(newPinnedPersons),
				dealId
			);
		},
		[dealId, pinnedPersons]
	);

	const personsListVirtualizer = useVirtualizer({
		count: sortedPersons.length,
		overscan: 5,
		getScrollElement: () => ref.current,
		estimateSize: () => ESTIMATED_PERSON_CARD_MAX_HEIGHT,
		measureElement: (el) => {
			// Fallback height is required bcs when react suspense displays a fallback, getBoundingClientRect returns 0.
			// this causes virtualizer to go in dead loop until suspense is finished, which most likely will break app.
			return el.getBoundingClientRect().height || ESTIMATED_PERSON_CARD_MAX_HEIGHT;
		},
	});

	const virtualizedItems = personsListVirtualizer.getVirtualItems();

	const hasReachedEndOfList =
		!persons?.length ||
		(persons?.length && personsListVirtualizer.range?.endIndex === (persons?.length ?? 0) - 1);

	//scroll to selected person if not in view
	useEffect(() => {
		if (!selectedPersonId || !sortedPersons.length) {
			return;
		}

		const index = sortedPersons.findIndex((person) => person.id === selectedPersonId);
		if (index === -1) {
			return;
		}

		const visibleItems = personsListVirtualizer.getVirtualItems();
		const isVisible = visibleItems.some((item) => item.index === index);

		if (!isVisible) {
			personsListVirtualizer.scrollToIndex(index, { align: 'start' });
		}
	}, [selectedPersonId, sortedPersons]);

	//set first person as selected when entering page
	useEffect(() => {
		if (!selectedPersonId && sortedPersons.length > 0 && !isCreateMode) {
			setSelectedPersonId(sortedPersons[0].id);
		}
	}, [selectedPersonId, sortedPersons, setSelectedPersonId, isCreateMode]);

	if (!sortedPersons.length && !isCreateMode) {
		return (
			<EmptyState icon="es-post-box" iconSize="sm">
				<PermissionWrapper
					resourceType={ResourceType.DEAL}
					resourceId={defaultToEmptyString(dealId)}
					permissionRole={PermissionRole.EDITOR}
				>
					<Button
						leftSection={<Icon variant="add" size="lg" />}
						onClick={onAddPersonClick}
					>
						{tCapManage('sidebar.create_entity_menu.create_new')}
					</Button>
				</PermissionWrapper>
			</EmptyState>
		);
	}

	return (
		//these styles required by react-virtualizer. Changes may break virtualizer
		<StyledVirtualizedListContainer
			ref={ref}
			style={{ height: maxHeight, overflow: 'auto' }}
			data-testid={TEST_ELEMENT_IDS.CAP_SIDEBAR_PERSONS_LIST_CONTAINER}
		>
			<div
				style={{
					height: personsListVirtualizer.getTotalSize(),
					position: 'relative',
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						transform: `translateY(${virtualizedItems[0]?.start ?? 0}px)`,
					}}
				>
					{personsListVirtualizer.getVirtualItems().map((virtualRow) => {
						const person = sortedPersons[virtualRow.index];
						return (
							<div
								key={virtualRow.key as string}
								data-index={virtualRow.index}
								ref={personsListVirtualizer.measureElement}
								style={{ marginBottom: '0.25rem' }}
							>
								<PersonCard
									isSelected={person.id === selectedPersonId}
									key={person.id}
									person={person}
									onCardClick={setSelectedPersonId}
									showWarning={person.hasSecuritiesWithInvalidTransactions}
									warningMessage={tCapManage('sidebar.invalid_transaction')}
									isPinned={pinnedPersons.has(person.id)}
									onPinValueChange={onPinValueChange}
									showCheckbox={isManageMode}
									personCheckboxValue={selectedPersons.has(person.id)}
									onPersonCheckboxChange={onPersonCheckboxChange}
								/>
							</div>
						);
					})}
				</div>
			</div>
			{!hasReachedEndOfList && (
				<StyledLoadMoreContainer>
					<Badge
						color="blue.6"
						variant="outline"
						radius="xl"
						bg="blue.0"
						leftSection={<Icon variant="arrow-down" size="md" />}
					>
						<Text fz="sm" fw={400}>
							Load more
						</Text>
					</Badge>
				</StyledLoadMoreContainer>
			)}
		</StyledVirtualizedListContainer>
	);
};

const Memoized = memo(PersonsList);

export { Memoized as PersonsList };
